import {
  deleteRfq,
  fetchRfqs,
  fetchSearchRfqs,
  restoreRfq,
  searchRfq,
  searchRfqWithDate,
  trashedRfq,
} from "actions/rfqActions";
import { Tooltip } from "flowbite-react";
import Loading from "components/Loading";
import StaticPopup from "components/StaticPopup";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import rfqService from "services/rfqService";
import { PaginationSection } from "views/CompanyDirectory/components";
import StaticPopupNew from "components/StaticPopupNew";

const statusShowByColor = (status) => {
  switch (status) {
    case "DRAFT": {
      return (
        <div className="w-fit bg-yellow-200 text-yellow-950 text-xs font-medium mr-2 px-2.5 py-0.5 rounded dark:bg-green-100 dark:text-green-800">
          Draft
        </div>
      );
    }
    case "CLOSED": {
      return (
        <div className="w-fit bg-red-200 text-red-950 text-xs font-medium mr-2 px-2.5 py-0.5 rounded dark:bg-green-100 dark:text-green-800">
          {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
        </div>
      );
    }
    case "CANCELED": {
      return (
        <div className="w-fit bg-gray-200 text-gray-950 text-xs font-medium mr-2 px-2.5 py-0.5 rounded dark:bg-green-100 dark:text-green-800">
          {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
        </div>
      );
    }
    case "DELETED": {
      return (
        <div className="w-fit bg-cyan-200 text-cyan-950 text-xs font-medium mr-2 px-2.5 py-0.5 rounded dark:bg-green-100 dark:text-green-800">
          {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
        </div>
      );
    }
    case "EXPIRED": {
      return (
        <div className="w-fit bg-red-200 text-red-950 text-xs font-medium mr-2 px-2.5 py-0.5 rounded dark:bg-green-100 dark:text-green-800">
          {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
        </div>
      );
    }
    case "HOLD": {
      return (
        <div className="w-fit bg-purple-200 text-purple-950 text-xs font-medium mr-2 px-2.5 py-0.5 rounded dark:bg-green-100 dark:text-green-800">
          {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
        </div>
      );
    }
    case "OPEN": {
      return (
        <div className="w-fit bg-green-100 text-green-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded dark:bg-green-100 dark:text-green-800">
          Open
        </div>
      );
    }
    case "PENDING APPROVAL": {
      return (
        <div className="w-fit bg-orange-200 text-orange-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded dark:bg-orange-100 dark:text-orange-800">
          Pending Approval
        </div>
      );
    }
    case "REWORK": {
      return (
        <div className="w-fit bg-lime-200 text-lime-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded dark:bg-lime-100 dark:text-lime-800">
          Rework
        </div>
      );
    }
    default: {
      return (
        <div>
          {" "}
          {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
        </div>
      );
    }
  }
};

const getRfqSuppliers = (row) => {
  const suppliers = [
    ...(Array.isArray(row?.suppliers) ? row.suppliers : []),
    ...(Array.isArray(row?.deletedSuppliers) ? row.deletedSuppliers : []),
  ];
  const seenSupplierIds = new Set();

  return suppliers.filter((supplier) => {
    const supplierKey = supplier?.supplierId || supplier?._id || supplier?.businessName;
    if (!supplierKey) return true;

    const normalizedKey = String(supplierKey);
    if (seenSupplierIds.has(normalizedKey)) return false;
    seenSupplierIds.add(normalizedKey);
    return true;
  });
};

const hasSupplierQuotation = (supplier = {}) =>
  Boolean(
    supplier?.quotationReceived ||
      supplier?.quotationDetails?.received ||
      supplier?.vendorQuotations?.quotationSets?.length
  );

const RfqsTableSection = ({
  handleGetRfqs,
  entityId,
  userIds,
  rfqsData,
  rfqFilterStatus,
  user,
  workspaceView,
  dateFilterOn,
  fetchFilterDateRfq,
  startDate,
  endDate,
  projectView = false,
  projectId,
  isApprovalRequired,
  getWorkspaceType,
  teamUserIdsInString,
  hideToolbar = false,
}) => {
  const navigate = useNavigate();
  // Calculate rfqsData?.rfqs directly without using state for rfqs
  // const rfqsData?.rfqs = (rfqsData?.rfqs ?? []).filter((rfq) => {
  //   if (workspaceView === "Pending Approvals") {
  //     if (rfq.status !== "PENDING APPROVAL") return false;

  //     const approvalDetails = rfq.approvalDetails ?? [];

  //     // Find the approval level(s) where the current user is an approver
  //     const userApprovalLevel = approvalDetails.find(
  //       (approval) =>
  //         approval.approvers?.some((a) => a.userId === user?.userId)
  //     );

  //     if (!userApprovalLevel) return false; // user not an approver at all

  //     const userLevelNumber = userApprovalLevel.level;

  //     // Ensure user's level is pending
  //     if (userApprovalLevel.status !== "PENDING") return false;

  //     // Check all previous levels are approved
  //     const previousLevelsApproved = approvalDetails
  //       .filter((approval) => approval.level < userLevelNumber)
  //       .every((approval) => approval.status === "APPROVED");

  //     // If user is at level 1, they can see it regardless
  //     if (userLevelNumber === 1) return true;

  //     // Otherwise, only show if all previous are approved
  //     return previousLevelsApproved;
  //   }

  //   // For other workspace views, show all RFQs
  //   return true;
  // });

  const dispatch = useDispatch();
  const searchRef = useRef(null);

  const [searchValue, setSearchValue] = useState("");
  const [searchKey, setSearchKey] = useState("");
  const [rfqs, setRfqs] = useState(rfqsData?.rfqs ?? []);
  const [currentPage, setCurrentPage] = useState(1);
  const [openRfqDeletePopup, setOpenRfqDeletePopup] = useState(false);
  const [rfqsErrors, setRfqsErrors] = useState({
    error: false,
    errorMassage: "",
  });
  const [openPoIdPopup, setOpenPoIdPopup] = useState(false);
  const [listOfPoIds, setListOfPoIds] = useState([]);
  const [openIndentPopup, setOpenIndentPopup] = useState(false);
  const [indentNumbersList, setIndentNumbersList] = useState([]);
  const [loading, setLoading] = useState(rfqsData?.loading);
  const [loadingDeleteRfq, setLoadingDeleteRfq] = useState(rfqsData?.loading);
  const [deleteRfqIdData, setDeleteRfqIdData] = useState({});
  const [deleteRfqStatus, setDeleteRfqStatus] = useState();
  const [openAwardedVendors, setOpenAwardedVendors] = useState(false);
  const [awardedVendorsList, setAwardedVendorsList] = useState([]);

  const rowInfo = [
    "Indent Number",
    "RFQ No.",
    "RFQ Subject",
    "Create Date",
    "RFQ Age",
    "Project Name",
    "RFQ Type",
    // "Close Date",
    // "PO ID",
    // "No. Of Vendor Selected",
    "Invited/Quotation",
    "Status",
    "Vendor Awarded",
    "Closure Value",
    "Pending With",
    "PO/WO Number",
    "Category",
    "Closed date",
    "Action",
  ];
  const rows =
    workspaceView === "My Workspace"
      ? projectView
        ? rowInfo?.filter((data) => data !== "ACTION")
        : rowInfo
      : [
          "Indent Number",
          "RFQ NO.",
          "RFQ Subject",
          "Create Date",
          "Created By",
          "RFQ Age",
          "Project Name",
          "RFQ Type",

          // "Close Date",
          // "PO ID",
          // "No. Of Vendor Selected",
          "Invited/Quotation",
          "Status",
          "Vendor Awarded",
          "Closure Value",
          "Pending With",
          "PO/WO Number",
          "Category",
          "Closed date",
          "Action",
        ];

  const stickyDividerClass = "border-r border-gray-200";
  const stickyHeaderBaseClass =
    "sticky z-[8] bg-gray-50 border-r border-gray-100";
  const stickyCellBaseClass =
    "sticky z-[4] bg-white border-r border-gray-100";

  const getStickyHeaderClass = (title) => {
    if (title === "Indent Number") {
      return `${stickyHeaderBaseClass} left-0 min-w-[136px] w-[136px]`;
    }

    if (title === "RFQ NO." || title === "RFQ No.") {
      return `${stickyHeaderBaseClass} left-[136px] min-w-[96px] w-[96px]`;
    }

    if (title === "RFQ Subject") {
      return `${stickyHeaderBaseClass} left-[232px] min-w-[144px] w-[144px]`;
    }

    if (title === "Create Date") {
      return `${stickyHeaderBaseClass} left-[376px] min-w-[120px] w-[120px] ${stickyDividerClass}`;
    }

    return "";
  };

  const getStickyCellClass = (columnName) => {
    if (columnName === "indentNumber") {
      return `${stickyCellBaseClass} left-0 min-w-[136px] w-[136px]`;
    }

    if (columnName === "rfqNo") {
      return `${stickyCellBaseClass} left-[136px] min-w-[96px] w-[96px]`;
    }

    if (columnName === "rfqSubject") {
      return `${stickyCellBaseClass} left-[232px] min-w-[144px] w-[144px]`;
    }

    if (columnName === "createdDate") {
      return `${stickyCellBaseClass} left-[376px] min-w-[120px] w-[120px] ${stickyDividerClass}`;
    }

    return "";
  };

  const stringUserIds = userIds.map((number) => String(number));
  const totalCount = rfqsData?.totalCount;
  const convertedData = rfqsData?.statusWiseCounts?.reduce((acc, curr) => {
    acc[curr._id] = curr?.count;
    return acc;
  }, {}) || {};

  const totalPages =
    rfqFilterStatus == ""
      ? rfqsData?.rfqs?.totalPages
      : convertedData[rfqFilterStatus] > 100
      ? Math.ceil(convertedData[rfqFilterStatus] / 100)
      : 1;

  const capitalizeFirstLetter = (string) => {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };
  const getRfqClosedDate = (row) => {
    if (row?.status !== "CLOSED") return null;
    return row?.awardeeDetails?.date || row?.rfqClosingDetails?.date || null;
  };

  const getIndentNumbers = (parentsRefId) =>
    String(parentsRefId || "")
      .split(/[,|]+/)
      .map((indent) => indent.trim())
      .filter(Boolean);

  const handleOpenIndentPopup = (indentNumbers = []) => {
    setIndentNumbersList(indentNumbers);
    setOpenIndentPopup(true);
  };

  const getRfqAgeDetails = (row) => {
    if (!row?.createdAt) return { label: "NA", isOverThreeDays: false };

    const createdDate = new Date(row.createdAt);
    if (Number.isNaN(createdDate.getTime())) {
      return { label: "NA", isOverThreeDays: false };
    }

    const endDateValue = getRfqClosedDate(row);
    const endDate = endDateValue ? new Date(endDateValue) : new Date();
    const endTime = Number.isNaN(endDate.getTime()) ? Date.now() : endDate.getTime();

    const totalMinutes = Math.max(
      0,
      Math.floor((endTime - createdDate.getTime()) / (1000 * 60))
    );
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;
    const isOverThreeDays = totalMinutes > 3 * 24 * 60;
    let label = "";

    if (days > 0) {
      label = `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      label = `${hours}h ${minutes}m`;
    } else {
      label = minutes > 0 ? `${minutes}m` : "<1m";
    }

    return { label, isOverThreeDays };
  };
  const handleOpenRfq = (rfqId, status) => {
    const canOpenInNewTab =
      user?.IAM_role === "Manager" && status === "PENDING APPROVAL";

    if (canOpenInNewTab) {
      window.open(`/rfqs/${rfqId}/Overview`, "_blank", "noopener,noreferrer");
      return;
    }

    navigate(`/rfqs/${rfqId}/Overview`);
  };

  const handleDeleteRfq = async () => {
    setLoadingDeleteRfq(true);
    if (deleteRfqStatus === "DELETED") {
      const response = await dispatch(
        restoreRfq({
          rfqId: deleteRfqIdData?.id,
          entityId,
          userIds: stringUserIds,
          currentStatus: rfqFilterStatus,
          actionBy: { userId: user?.userId, name: user?.name },
          startDate,
          endDate,
        })
      );
    } else if (deleteRfqStatus === "Delete") {
      const response = await dispatch(
        deleteRfq({
          rfqId: deleteRfqIdData?.id,
          entityId,
          userIds: stringUserIds,
          currentStatus: rfqFilterStatus,
          actionBy: { userId: user?.userId, name: user?.name },
          startDate,
          endDate,
        })
      );
    } else {
      const response = await dispatch(
        trashedRfq({
          rfqId: deleteRfqIdData?.id,
          entityId,
          userIds: stringUserIds,
          currentStatus: rfqFilterStatus,
          actionBy: { userId: user?.userId, name: user?.name },
          startDate,
          endDate,
        })
      );
    }
    setLoadingDeleteRfq(false);
    setOpenRfqDeletePopup(false);
  };

  const handleOpenDeleteRfqPopup = (rfqId, userId, status) => {
    setDeleteRfqIdData({ id: rfqId, userId });
    setDeleteRfqStatus(status);
    setOpenRfqDeletePopup(true);
  };

  const handlePermanentDeleteRfq = (rfqId, userId, status) => {
    setDeleteRfqIdData({ id: rfqId, userId });
    setDeleteRfqStatus(status);
    setOpenRfqDeletePopup(true);
  };

  const handleCloseDeleteRfqPopup = () => {
    setOpenRfqDeletePopup(false);
  };
  const handleOpenPoIdPopup = (suppliers) => {
    if (!suppliers || suppliers.length === 0) return;

    const formatted = suppliers
      .map((supplier) => {
        const poNumbers = supplier.poDetails
          ?.map((po) => po.poNumber)
          .filter(Boolean);
        if (!poNumbers || poNumbers.length === 0) return null;

        return `${
          supplier.businessName || "Unnamed Supplier"
        }: ${poNumbers.join(", ")}`;
      })
      .filter(Boolean);

    setListOfPoIds(formatted);
    setOpenPoIdPopup(true);
  };

  const handleClosePoIdPopup = () => {
    setOpenPoIdPopup(false);
  };

  const closePoIdPopup = () => {
    setOpenPoIdPopup(false);
    setListOfPoIds([]);
  };

  const lengthOfRfqAwardeeVendors = (supplier) => {
    let count = 0;
    supplier.forEach((rfq) => {
      if (rfq.closingDetails) {
        count++;
      }
    });
    return count;
  };

  const poIdList = (supplier) => {
    const list = supplier.map((rfq) => {
      if (rfq.closingDetails?.length >= 1) {
        return rfq?.closingDetails?.map((data) => data?.poNumber);
      }
      return [];
    });
    const filterList = list?.filter((rfq) => rfq?.length >= 1 && rfq);
    return filterList?.length == 0 ? ["--"] : filterList.flat();
  };

  const handleSearchChange = async (e) => {
    setSearchValue(e.target.value);
    if (e.target.value === "") {
      setLoading(true);
      await dispatch(
        searchRfqWithDate({
          projectId,
          entityId,
          searchParam: "",
          status: rfqFilterStatus,
          userIds: stringUserIds,
          startDate: rfqsData?.startDate,
          endDate: rfqsData?.endDate,
          startDateOfClosing: rfqsData?.startDateOfClosing,
          endDateOfClosing: rfqsData?.endDateOfClosing,
          dateFilterIsActive: rfqsData?.dateFilterIsActive,
          closeDateFilterIsActive: rfqsData?.closeDateFilterIsActive,
          rfqTypes: rfqsData?.rfqTypes,
          workspaceType: getWorkspaceType(workspaceView),
          approverId: user?.userId,
          teamUserIds: teamUserIdsInString,
        })
      );
      setSearchKey("");
      setLoading(false);
    }
  };

  const handleSearchRfqs = async () => {
    setLoading(true);
    const response = await dispatch(
      searchRfqWithDate({
        projectId,
        entityId,
        searchParam: searchValue,
        status: rfqFilterStatus,
        userIds: stringUserIds,
        startDate: rfqsData?.startDate,
        endDate: rfqsData?.endDate,
        startDateOfClosing: rfqsData?.startDateOfClosing,
        endDateOfClosing: rfqsData?.endDateOfClosing,
        dateFilterIsActive: rfqsData?.dateFilterIsActive,
        closeDateFilterIsActive: rfqsData?.closeDateFilterIsActive,
        rfqTypes: rfqsData?.rfqTypes,
        workspaceType: getWorkspaceType(workspaceView),
        approverId: user?.userId,
        teamUserIds: teamUserIdsInString,
      })
    );

    setLoading(false);
    setSearchKey(searchValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      setLoading(true);
      handleSearchRfqs();
      e.preventDefault();
    }
  };

  const handlePageChange = (pageNum) => {
    setCurrentPage(pageNum);

    dispatch(
      searchRfqWithDate({
        projectId,
        entityId,
        searchParam: searchKey,
        status: rfqFilterStatus,
        userIds: stringUserIds,
        startDate: rfqsData?.startDate,
        endDate: rfqsData?.endDate,
        startDateOfClosing: rfqsData?.startDateOfClosing,
        endDateOfClosing: rfqsData?.endDateOfClosing,
        dateFilterIsActive: rfqsData?.dateFilterIsActive,
        closeDateFilterIsActive: rfqsData?.closeDateFilterIsActive,
        rfqTypes: rfqsData?.rfqTypes,
        workspaceType: getWorkspaceType(workspaceView),
        approverId: user?.userId,
        teamUserIds: teamUserIdsInString,
        page: pageNum,
      })
    );
  };

  const handleAwardedClick = (suppliers) => {
    const awarded = suppliers?.filter((s) => s.isAwarded) || [];
    // Always clear and set fresh data when opening
    setAwardedVendorsList(awarded);
    setOpenAwardedVendors(true);
  };

  const getCurrentPendingApprover = (approvalDetails = []) => {
    if (!Array.isArray(approvalDetails)) return null;

    // Sort by approval level
    const sorted = [...approvalDetails].sort((a, b) => a.level - b.level);

    // Find the first PENDING level
    const currentPendingLevel = sorted.find(
      (level) => level.status === "PENDING"
    );

    return currentPendingLevel?.approvers?.find((approver) => approver?.name) || null;
  };

  useEffect(() => {
    setRfqs(rfqsData?.rfqs);
    setSearchValue(rfqsData?.searchParam ?? "");
    setCurrentPage(rfqsData?.currentPage);
    setSearchKey(rfqsData?.searchParam ?? "");
    setLoading(rfqsData?.loading);
    setLoadingDeleteRfq(rfqsData?.loading);
    setRfqsErrors({
      error: rfqsData?.error,
      errorMassage: rfqsData?.errorMessage,
    });
    setTimeout(() => {
      setRfqsErrors({
        error: false,
        errorMassage: "",
      });
    }, 6000);
  }, [rfqsData]);

  useEffect(() => {
    // when user click ctrl + f  then focus on search bar
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.key === "f") {
        event.preventDefault(); // Prevent default Ctrl + F behavior
        searchRef.current.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []); // Empty dependency array ensures this effect runs only once

  return (
    <div className="flex h-full min-h-0 flex-col rounded-xl bg-white">
      {!hideToolbar && (
        <section className="shrink-0 px-4 py-3">
          <div className=" mx-auto w-full">
            <div className="dark:bg-gray-800 sm:rounded-lg">
              <div className="flex flex-col items-center justify-between gap-3 md:flex-row">
                {/* showing results */}
                <div>
                  <p className="text-xs font-medium text-gray-600">
                    {rfqsData?.rfqs?.length > 0 && (
                      <>
                        {`Showing ${(rfqsData?.currentPage - 1) * 100 + 1}-${
                          (rfqsData?.currentPage - 1) * 100 +
                          rfqsData?.rfqs?.length
                        } of ${
                          rfqFilterStatus === ""
                            ? rfqsData?.totalCount
                            : rfqsData?.statusWiseCounts?.find(
                                (item) => item._id === rfqFilterStatus
                              )?.count || rfqsData?.totalCount
                        } RFQ${rfqsData?.rfqs?.length > 1 ? "s" : ""} `}
                        {searchKey && (
                          <span>
                            for{" "}
                            <span className="text-[#fd6600] font-bold">
                              "{searchKey}"
                            </span>
                          </span>
                        )}
                      </>
                    )}
                  </p>
                </div>

                {/* search */}
                <div className="w-full md:w-1/2">
                  <div className="flex items-center">
                    <label htmlFor="simple-search" className="sr-only">
                      Search
                    </label>
                    <div className="relative w-full">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <svg
                          aria-hidden="true"
                          className="w-5 h-5 text-gray-500 dark:text-gray-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <input
                        type="text"
                        id="simple-search"
                        ref={searchRef}
                        className="block w-full rounded-lg border border-gray-300 bg-[#FCFCFA] p-2 pl-10 text-xs text-gray-900 placeholder:text-xs focus:border-[#E95420] focus:ring-0 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                        placeholder="Search by Vendor Name or RFQ No."
                        value={searchValue}
                        onChange={handleSearchChange}
                        onKeyDown={handleKeyDown}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-[50vh]">
          <div className="loader ease-linear rounded-full border-8 border-t-8 border-gray-200 h-10 w-10"></div>
        </div>
      ) : (
        <>
          {rfqsData?.rfqs?.length >= 1 ? (
            <>
              <div
                id="scrollableDiv"
                className="relative rounded-xl min-h-0 flex-1 overflow-auto rfq-table-scrollbar"
                //onScroll={handleCloseDropDown} //we used position fixed to show dropdown when user try to scroll then that dropdown hidden
              >
                <table className="min-w-max w-full table-auto border-separate border-spacing-0 text-left text-xs text-gray-600 dark:text-gray-400">
                  <thead className="sticky top-0 z-[5] max-w-full bg-gray-50 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-400">
                    <tr>
                      {rows.map((title, index) => (
                        <th
                          key={index}
                          scope="col"
                          className={`border-b border-r border-gray-200 bg-gray-50 px-4 py-3 font-medium text-gray-700 last:border-r-0 ${getStickyHeaderClass(
                            title
                          )}`}
                        >
                          <p
                            className={`${
                              title === "RFQ NO." ||
                              title === "RFQ Age" ||
                              title === "VENDOR SELECTED" ||
                              // title ==="PO ID" ||
                              title === "INDENT NUMBER"
                                ? "text-right "
                                : "text-left "
                            }truncate`}
                          >
                            {title}
                          </p>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rfqsData?.rfqs?.map((row, rowIndex) => (
                      <tr key={rowIndex} className="text-gray-600 [&>td]:border-b [&>td]:border-r [&>td]:border-[#EEF0F2] last:[&>td]:border-b-0 [&>td:last-child]:border-r-0">
                        <td className={getStickyCellClass("indentNumber")}>
                          <div className="flex justify-end px-4 py-2">
                            {(() => {
                              const indentNumbers = getIndentNumbers(row?.parentsRefId);
                              const firstIndent = indentNumbers[0];
                              const hiddenCount = Math.max(indentNumbers.length - 1, 0);

                              if (!firstIndent) {
                                return <span className="text-xs text-gray-300">--</span>;
                              }

                              return (
                                <p className="truncate text-right text-xs text-gray-600 font-medium">
                                  <span>{firstIndent}</span>
                                  {hiddenCount > 0 && (
                                    <button
                                      type="button"
                                      className="ml-1 whitespace-nowrap text-blue-600 hover:underline"
                                      onClick={() => handleOpenIndentPopup(indentNumbers)}
                                    >
                                      +{hiddenCount} more
                                    </button>
                                  )}
                                </p>
                              );
                            })()}
                          </div>
                        </td>

                        {/* rfq no. */}
                        <td className={getStickyCellClass("rfqNo")}>
                          <div className="flex  justify-end px-4 py-2">
                            <p className="text-right text-xs text-gray-600 font-medium">
                              {row?.rfqId}
                            </p>
                          </div>
                        </td>

                        {/* rfq subject */}
                        <td className={getStickyCellClass("rfqSubject")}>
                          <div className="max-w-[144px] px-4 py-2">
                            <p
                              className="truncate overflow-ellipsis overflow-hidden text-xs text-blue-500 cursor-pointer font-medium"
                              onClick={() => handleOpenRfq(row?.rfqId, row?.status)}
                            >
                              {row?.name}
                            </p>
                          </div>
                        </td>

                        {/* create date */}
                        <td className={getStickyCellClass("createdDate")}>
                          <div className="flex  justify-start px-4 py-2">
                            <p className="text-left  text-xs text-gray-600 font-medium">
                              {row?.createdAt
                                ? new Date(row?.createdAt).toLocaleDateString(
                                    "en-GB"
                                  )
                                : "NA"}
                            </p>
                          </div>
                        </td>

                        {workspaceView != "My Workspace" && (
                          <td className={getStickyCellClass("createdBy")}>
                            <div className="flex  justify-start px-4 py-2">
                              <p className="text-left truncate text-xs text-gray-600 font-medium">
                                {row?.requesterName}
                              </p>
                            </div>
                          </td>
                        )}

                        {/* rfq age */}
                        <td className={getStickyCellClass("rfqAge")}>
                          <div className="flex justify-end px-4 py-2">
                            {(() => {
                              const rfqAge = getRfqAgeDetails(row);

                              return (
                                <p
                                  className={`whitespace-nowrap text-right text-xs font-medium ${
                                    String(row?.status || "").toUpperCase() !== "CLOSED" &&
                                    rfqAge.isOverThreeDays
                                      ? "text-red-600"
                                      : "text-gray-600"
                                  }`}
                                >
                                  {rfqAge.label}
                                </p>
                              );
                            })()}
                          </div>
                        </td>

                        {/* {workspaceView === "Approved RFQs" && (
                          <td>
                            <div className="flex justify-start px-4 py-2">
                              <p className="text-left text-xs text-gray-600 font-medium">
                                {(() => {
                                  const approvedDetail =
                                    row?.approvalDetails?.find(
                                      (approval) =>
                                        approval.status === "APPROVED" &&
                                        approval.approvedBy?.userId ===
                                          user?.userId
                                    );
                                  return approvedDetail?.approvedAt?.$date ? (
                                    new Date(
                                      approvedDetail.approvedAt.$date
                                    ).toLocaleDateString("en-GB")
                                  ) : (
                                    <span className="text-gray-300">--</span>
                                  );
                                })()}
                              </p>
                            </div>
                          </td>
                        )} */}

                        <td>
                          <div className="w-48 px-4 py-2">
                            {row?.projectName?.includes("||") ? (
                              <div className="flex flex-col gap-1">
                                {row.projectName
                                  .split("||")
                                  .map((project, i) => (
                                    <p
                                      key={i}
                                      className="text-xs text-gray-600 font-medium"
                                    >
                                      {project.trim()}
                                    </p>
                                  ))}
                              </div>
                            ) : (
                              <p className="text-xs text-gray-600 font-medium">
                                {row?.projectName}
                              </p>
                            )}
                          </div>
                        </td>

                        {/* rfq type */}
                        <td className="px-4 py-2">
                          {capitalizeFirstLetter(row?.rfqType)}
                        </td>

                        <td className="text-right px-4 py-2 ">
                          {(() => {
                            const rfqSuppliers = getRfqSuppliers(row);
                            return `${rfqSuppliers.length}/${rfqSuppliers.filter(hasSupplierQuotation).length}`;
                          })()}
                        </td>

                        {/* <td className="text-right px-4 py-2">{row?.suppliers?.length}</td> */}
                        <td>
                          <div className="flex  justify-start px-4 py-2">
                            {/* <p className=" text-center text-xs text-gray-600 font-medium">
                      {row?.status}
                    </p> */}
                            {statusShowByColor(row?.status)}
                          </div>
                        </td>

                      {/* vendor awarded */}
                        <td>
                          <div className="flex px-4 py-2">
                            <p className="truncate text-xs text-gray-600 font-medium">
                              {row?.status === "CLOSED" ? (
                                (() => {
                                  const awarded =
                                    row?.suppliers?.filter(
                                      (s) => s.isAwarded
                                    ) || [];
                                  if (awarded.length === 0) {
                                    return (
                                      <span className="text-gray-300">--</span>
                                    );
                                  }

                                  const first = awarded[0];
                                  return (
                                    <span
                                      className={
                                        awarded.length > 1
                                          ? "cursor-pointer hover:underline truncate"
                                          : ""
                                      }
                                      onClick={() =>
                                        awarded.length > 1 &&
                                        handleAwardedClick(row.suppliers)
                                      }
                                    >
                                      {first.businessName}
                                      {/* <br /> */}
                                      {/* {first.contactDetails[0]?.mobile} */}
                                      {first.closingDetails[0]?.amount && (
                                        <>
                                          <br />
                                          Rs.
                                          {new Intl.NumberFormat("en-IN", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          }).format(
                                            Number(
                                              first.closingDetails[0].amount
                                            )
                                          )}
                                        </>
                                      )}
                                      {awarded.length > 1 && (
                                        <span className="text-blue-500 ml-1">
                                          +{awarded.length - 1} more
                                        </span>
                                      )}
                                    </span>
                                  );
                                })()
                              ) : (
                                <span className="text-gray-300">--</span>
                              )}
                            </p>
                          </div>
                        </td>

                        <td>
                          <div className="flex justify-end px-4 py-2">
                            <p className="truncate text-right text-xs text-gray-600 font-medium">
                              {row?.status === "CLOSED" ||
                              row?.status === "PENDING APPROVAL" ? (
                                (() => {
                                  const awardedSuppliers =
                                    row?.suppliers?.filter(
                                      (s) => s.isAwarded
                                    ) || [];
                                  if (awardedSuppliers.length === 0) {
                                    return (
                                      <span className="text-gray-300">--</span>
                                    );
                                  }

                                  // Calculate total closure value
                                  const totalClosureValue =
                                    awardedSuppliers.reduce((sum, supplier) => {
                                      const amount =
                                        parseFloat(
                                          supplier.closingDetails[0]?.amount
                                        ) || 0;
                                      return sum + amount;
                                    }, 0);

                                  return (
                                    <>
                                      Rs.
                                      {new Intl.NumberFormat("en-IN", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }).format(totalClosureValue)}
                                    </>
                                  );
                                })()
                              ) : (
                                <span className="text-gray-300">--</span>
                              )}
                            </p>
                          </div>
                        </td>

                        {/* <td>
                          <div className="flex justify-end px-4 py-2">
                            <p className="truncate text-right text-xs text-gray-600 font-medium">
                              {row?.status === "PENDING APPROVAL" ? (
                                row?.approvalDetails?.find(
                                  (approval) => approval.status === "PENDING"
                                )?.approvers[0]?.name || (
                                  <span className="text-gray-300">--</span>
                                )
                              ) : (
                                <span className="text-gray-300">--</span>
                              )}
                            </p>
                          </div>
                        </td> */}
                        {/* pending with */}
                        <td>
                          <div className="flex px-4 py-2">
                            <p className="truncate text-xs text-gray-600 font-medium">
                              {row?.status === "PENDING APPROVAL" ? (
                                (() => {
                                  const currentApprover =
                                    getCurrentPendingApprover(
                                      row?.approvalDetails
                                    );

                                  if (!currentApprover) {
                                    return (
                                      <span className="text-gray-300">--</span>
                                    );
                                  }

                                  return <span>{currentApprover.name}</span>;
                                })()
                              ) : (
                                <span className="text-gray-300">--</span>
                              )}
                            </p>
                          </div>
                        </td>

                        <td>
                          <div className="flex justify-end px-4 py-2">
                            <div className="text-right text-xs text-gray-600 font-medium">
                              {row?.suppliers?.length > 0 ? (
                                (() => {
                                  const allPOs = row.suppliers
                                    .flatMap(
                                      (supplier) => supplier.poDetails || []
                                    )
                                    .map((po) => po.poNumber)
                                    .filter(Boolean);

                                  if (allPOs.length === 0) {
                                    return (
                                      <span className="text-gray-300">--</span>
                                    );
                                  }

                                  const visiblePOs = allPOs.slice(0, 2);
                                  const hiddenCount =
                                    allPOs.length - visiblePOs.length;

                                  return (
                                    <span>
                                      {visiblePOs.join(", ")}
                                      {hiddenCount > 0 && (
                                        <>
                                          {" "}
                                          <br />
                                          <span
                                            className="text-blue-500 cursor-pointer hover:underline"
                                            onClick={() =>
                                              handleOpenPoIdPopup(row.suppliers)
                                            }
                                          >
                                            +{hiddenCount} more
                                          </span>
                                        </>
                                      )}
                                    </span>
                                  );
                                })()
                              ) : (
                                <span className="text-gray-300">--</span>
                              )}
                            </div>
                          </div>
                        </td>

                          {/* category */}
                        <td className="px-4 py-2">
                           {row?.businessDivision ? (
                                row?.businessDivision
                              ) : (
                                <span className="text-gray-300">--</span>
                              )}
                        </td>

                        <td>
                          <div className="flex  justify-start px-4 py-2">
                            <p className="text-left  text-xs text-gray-600 font-medium">
                              {row?.status === "CLOSED" &&
                              row?.awardeeDetails ? (
                                new Date(
                                  row?.awardeeDetails?.date
                                ).toLocaleDateString("en-GB")
                              ) : row?.status === "CLOSED" &&
                                row?.rfqClosingDetails ? (
                                new Date(
                                  row?.rfqClosingDetails?.date
                                ).toLocaleDateString("en-GB")
                              ) : (
                                <span className="text-gray-300">--</span>
                              )}
                            </p>
                          </div>
                        </td>

                        {/* <td>
                          <div className="flex  justify-start px-4 py-2">
                            {poIdList(row?.suppliers)?.length <= 1 ? (
                              <p className=" text-left text-xs text-gray-600 font-medium">
                                {poIdList(row?.suppliers)[0] === "--" ? (
                                  <span className="text-gray-300">--</span>
                                ) : (
                                  poIdList(row?.suppliers)
                                )}
                              </p>
                            ) : (
                              <p className=" truncate text-left text-xs text-gray-600 font-medium">
                                {poIdList(row?.suppliers)[0]}
                                {", "}
                                <span
                                  className="text-gray-600 underline cursor-pointer ml-1 text-xs"
                                  onClick={() =>
                                    handleOpenPoIdPopup(row?.suppliers)
                                  }
                                >
                                  +{poIdList(row?.suppliers)?.length - 1} more
                                </span>
                              </p>
                            )}
                          </div>
                        </td> */}

                        {/* <td className=" px-4 py-2">
                          <div className="flex justify-end">
                            <p className="pr-4 text-right text-xs text-gray-600  font-medium">
                              {row?.suppliers?.length ?? (
                                <span className="text-gray-300">--</span>
                              )}
                            </p>
                          </div>
                        </td> */}
                        {!projectView && (
                          <td>
                            <div className="flex  justify-start px-4 py-2 font-medium text-blue-700">
                              {row?.status === "DELETED" ? (
                                <div className="flex gap-2">
                                  <Tooltip content="Permanent Delete">
                                    <button
                                      onClick={() =>
                                        handlePermanentDeleteRfq(
                                          row?.rfqId,
                                          row?.requesterId,
                                          "Delete"
                                        )
                                      }
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium MuiSvgIcon-root MuiSvgIcon-fontSizeLarge css-p79yt4"
                                        viewBox="0 0 24 24"
                                        focusable="false"
                                        width="18px"
                                        height="18px"
                                        aria-hidden="true"
                                      >
                                        <path
                                          fill="#4B5563"
                                          d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6zM19 4h-3.5l-1-1h-5l-1 1H5v2h14z"
                                        />
                                      </svg>
                                    </button>
                                  </Tooltip>

                                  <Tooltip content="Restore">
                                    <button
                                      onClick={() =>
                                        handleOpenDeleteRfqPopup(
                                          row?.rfqId,
                                          row?.requesterId,
                                          row?.status
                                        )
                                      }
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 512 512"
                                        width="18px"
                                        height="18px"
                                      >
                                        <path
                                          fill="green"
                                          d="M174.7 45.1C192.2 17 223 0 256 0s63.8 17 81.3 45.1l38.6 61.7 27-15.6c8.4-4.9 18.9-4.2 26.6 1.7s11.1 15.9 8.6 25.3l-23.4 87.4c-3.4 12.8-16.6 20.4-29.4 17l-87.4-23.4c-9.4-2.5-16.3-10.4-17.6-20s3.4-19.1 11.8-23.9l28.4-16.4L283 79c-5.8-9.3-16-15-27-15s-21.2 5.7-27 15l-17.5 28c-9.2 14.8-28.6 19.5-43.6 10.5c-15.3-9.2-20.2-29.2-10.7-44.4l17.5-28zM429.5 251.9c15-9 34.4-4.3 43.6 10.5l24.4 39.1c9.4 15.1 14.4 32.4 14.6 50.2c.3 53.1-42.7 96.4-95.8 96.4L320 448v32c0 9.7-5.8 18.5-14.8 22.2s-19.3 1.7-26.2-5.2l-64-64c-9.4-9.4-9.4-24.6 0-33.9l64-64c6.9-6.9 17.2-8.9 26.2-5.2s14.8 12.5 14.8 22.2v32l96.2 0c17.6 0 31.9-14.4 31.8-32c0-5.9-1.7-11.7-4.8-16.7l-24.4-39.1c-9.5-15.2-4.7-35.2 10.7-44.4zm-364.6-31L36 204.2c-8.4-4.9-13.1-14.3-11.8-23.9s8.2-17.5 17.6-20l87.4-23.4c12.8-3.4 26 4.2 29.4 17L182 241.2c2.5 9.4-.9 19.3-8.6 25.3s-18.2 6.6-26.6 1.7l-26.5-15.3L68.8 335.3c-3.1 5-4.8 10.8-4.8 16.7c-.1 17.6 14.2 32 31.8 32l32.2 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-32.2 0C42.7 448-.3 404.8 0 351.6c.1-17.8 5.1-35.1 14.6-50.2l50.3-80.5z"
                                        />
                                      </svg>
                                    </button>{" "}
                                  </Tooltip>
                                </div>
                              // ) : row?.status === "CLOSED" ? (
                              //   <button disabled={true}>
                              //     <svg
                              //       xmlns="http://www.w3.org/2000/svg"
                              //       className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium MuiSvgIcon-root MuiSvgIcon-fontSizeLarge css-p79yt4"
                              //       viewBox="0 0 24 24"
                              //       focusable="false"
                              //       width="18px"
                              //       height="18px"
                              //       aria-hidden="true"
                              //     >
                              //       <path
                              //         fill="#86878b"
                              //         d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6zM19 4h-3.5l-1-1h-5l-1 1H5v2h14z"
                              //       />
                              //     </svg>
                              //   </button>
                              ) : (
                                <Tooltip content="Delete">
                                  <button
                                    onClick={() =>
                                      handleOpenDeleteRfqPopup(
                                        row?.rfqId,
                                        row?.requesterId,
                                        row?.status
                                      )
                                    }
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium MuiSvgIcon-root MuiSvgIcon-fontSizeLarge css-p79yt4"
                                      viewBox="0 0 24 24"
                                      focusable="false"
                                      width="18px"
                                      height="18px"
                                      aria-hidden="true"
                                    >
                                      <path
                                        fill="#4B5563"
                                        d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6zM19 4h-3.5l-1-1h-5l-1 1H5v2h14z"
                                      />
                                    </svg>
                                  </button>
                                </Tooltip>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <style>
                {`
          .rfq-table-scrollbar::-webkit-scrollbar {
            height: 4px;
            width: 6px;
          }

          .rfq-table-scrollbar::-webkit-scrollbar-thumb {
            background-color: #9A9A9A;
            border-radius: 999px;
          }

          .rfq-table-scrollbar::-webkit-scrollbar-track {
            background-color: #EAF7F6;
          }
        `}
              </style>
              {searchValue === "" && (
                <div className="sticky w-full bottom-0 bg-white left-0">
                  <PaginationSection
                    totalPages={totalPages}
                    currentPage={currentPage}
                    handlePageChange={handlePageChange}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="w-full h-[50vh] flex justify-center items-center">
              {searchValue === "" ? (
                <h6>
                  {user?.IAM_role === "Manager" &&
                  isApprovalRequired &&
                  workspaceView === "Pending Approvals"
                    ? "No requirements for approval."
                    : "No results found create some rfqs"}
                </h6>
              ) : (
                <h6>No results found </h6>
              )}
            </div>
          )}
        </>
      )}
      <StaticPopup
        openModal={openRfqDeletePopup}
        handleClose={() => handleCloseDeleteRfqPopup()}
        header={deleteRfqStatus === "DELETED" ? "Restore RFQ" : "Delete RFQ"}
      >
        {deleteRfqStatus === "DELETED" ? (
          <p>Are you sure you want to restore this RFQ?</p>
        ) : (
          <p>Are you sure you want to delete this RFQ?</p>
        )}
        <div className="flex gap-4 mt-auto">
          <button
            className="p-2 px-4 border rounded-lg text-white bg-blue-700 hover:bg-blue-800 font-semibold "
            onClick={handleDeleteRfq}
          >
            {loadingDeleteRfq ? <Loading /> : "Yes"}
          </button>
          <button
            className="p-2 px-4 border rounded-lg font-semibold hover:bg-slate-100 "
            onClick={handleCloseDeleteRfqPopup}
          >
            No
          </button>
        </div>
        {rfqsErrors?.error && (
          <p className="text-xs text-red-700">{rfqsErrors?.errorMassage}</p>
        )}
      </StaticPopup>

      <StaticPopup
        openModal={openPoIdPopup}
        handleClose={closePoIdPopup}
        header={"PO Id"}
        width="md"
        height="auto"
        size="sm"
      >
        <p className="text-xs text-gray-600 my-2 ">{listOfPoIds?.join(", ")}</p>
      </StaticPopup>

      <StaticPopupNew
        header="PO/WO Numbers"
        bodyStyle="pb-0 pt-0 max-h-[70vh] overflow-y-auto"
        openModal={openPoIdPopup}
        handleClose={handleClosePoIdPopup}
      >
        <div className="space-y-4 p-4">
          {listOfPoIds.length > 0 ? (
            listOfPoIds.map((line, index) => {
              const [supplier, poList] = line.split(": ");
              return (
                <div
                  key={`po-${index}`}
                  className="border-b border-gray-200 pb-3 last:border-0"
                >
                  <p className="font-medium">{supplier}</p>
                  <p className="text-sm text-gray-700">{poList}</p>
                </div>
              );
            })
          ) : (
            <p className="text-sm text-gray-400">No PO IDs found.</p>
          )}
        </div>
      </StaticPopupNew>

      <StaticPopupNew
        header="Awarded Vendors"
        bodyStyle="pb-0 pt-0 max-h-[70vh] overflow-y-auto"
        openModal={openAwardedVendors}
        handleClose={() => setOpenAwardedVendors(false)}
      >
        <div className="space-y-4 p-4">
          {awardedVendorsList.map((vendor, index) => (
            <div
              key={`${vendor._id}-${index}`}
              className="border-b border-gray-200 pb-3 last:border-0"
            >
              <p className="font-medium">{vendor.businessName}</p>
              {/* <p className="text-sm">
                Contact: {vendor.contactDetails[0]?.mobile || "N/A"}
              </p> */}
              <p className="text-sm">
                Amount:{" "}
                {/* {vendor.closingDetails[0]?.amount
                  ? `₹${vendor.closingDetails[0].amount}`
                  : "N/A"} */}
                Rs.
                {vendor.closingDetails[0]?.amount
                  ? new Intl.NumberFormat("en-IN").format(
                      Number(vendor.closingDetails[0].amount).toFixed(2)
                    )
                  : "N/A"}
              </p>
            </div>
          ))}
        </div>
      </StaticPopupNew>

      <StaticPopupNew
        header="Indent Numbers"
        bodyStyle="pb-0 pt-0 max-h-[70vh] overflow-y-auto"
        openModal={openIndentPopup}
        handleClose={() => setOpenIndentPopup(false)}
      >
        <div className="grid grid-cols-1 gap-2 p-4 sm:grid-cols-2 lg:grid-cols-3">
          {indentNumbersList.length > 0 ? (
            indentNumbersList.map((indentNumber, index) => (
              <div
                key={`${indentNumber}-${index}`}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700"
              >
                {indentNumber}
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-400">No indent numbers found.</p>
          )}
        </div>
      </StaticPopupNew>
    </div>
  );
};

export default RfqsTableSection;
